import React, { Component } from "react";
import {
  Avatar,
  Table,
  Tooltip,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  TableBody,
  TableContainer,
} from "@mui/material";
import {
  AccountBalance,
  Delete,
  HourglassEmpty,
  VerifiedUser,
  PersonRounded,
} from "@mui/icons-material";
import { Spinner } from "reactstrap";
import { AlertDialog } from "../../shared/AlertDialog";
import { RouteComponentProps } from "react-router";
import { User, UsersAdminService } from "../../../generated";

export interface State {
  loading: boolean;
  items: User[];
  mode: string;
  headline: string;
  userEmailToDelete: string;
  openAlertDialog: boolean;
  titleAlertDialog: string;
  contentTextAlertDialog: string;
}

export class AdminManageUsers extends Component<RouteComponentProps, State> {
  constructor(props: RouteComponentProps) {
    super(props);

    this.state = {
      loading: true,
      items: [],
      mode: this.props.location.pathname.split("/").pop(),
      headline: "Testers",
      userEmailToDelete: null,
      openAlertDialog: false,
      titleAlertDialog: null,
      contentTextAlertDialog: null,
    };
  }

  async componentDidMount() {
    await this.getUsers();
  }

  async getUsers() {
    switch (this.state.mode) {
      case "active":
        await this.getActiveUsers();
        break;
      case "invited":
        await this.getInvitedUsers();
        break;
      default:
        await this.getAllUsers();
        break;
    }
  }

  async getAllUsers() {
    this.setState({
      loading: true,
      headline: "All Testers",
    });

    const result = await UsersAdminService.usersAdminGetAll({});
    this.setState({
      items: result,
      loading: false,
    });
  }

  async getInvitedUsers() {
    this.setState({
      loading: true,
      headline: "Invited Testers",
    });

    const result = await UsersAdminService.usersAdminGetAllInvitedUsers();
    this.setState({
      loading: false,
      items: result,
    });
  }

  async getActiveUsers() {
    this.setState({
      loading: true,
      headline: "Active Testers",
    });

    const result = await UsersAdminService.usersAdminGetAllActiveUsers();
    this.setState({
      items: result,
      loading: false,
    });
  }

  async deleteUser(email: string) {
    this.setState({
      loading: true,
    });

    try {
      await UsersAdminService.usersAdminDeleteUser({ email });
      await this.getUsers();
    } catch {
      //ignored
    }
  }

  handleUserProfileClick = (user: User) => {
    this.props.history.push(`/admin/user/` + user.email);
  };

  handleBanksListClick = (user: User) => {
    this.props.history.push(`/admin/users/banks/` + user.email);
  };

  handleDeleteUserClick = (user: User) => {
    this.setState({
      openAlertDialog: true,
      titleAlertDialog: null,
      contentTextAlertDialog: "Do you want to delete " + user.email + "?",
      userEmailToDelete: user.email,
    });
  };

  handleCloseAlertDialog = async (result: boolean) => {
    if (result && this.state.userEmailToDelete !== null) {
      await this.deleteUser(this.state.userEmailToDelete);
      this.setState({
        userEmailToDelete: null,
        openAlertDialog: false,
      });
    } else {
      this.setState({
        openAlertDialog: false,
        loading: false,
      });
    }
  };

  renderEmptyRow() {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          Nothing here
        </TableCell>
      </TableRow>
    );
  }

  renderRows(users: User[]) {
    return users.map((user) => (
      <TableRow key={user.rowKey}>
        <TableCell>
          <Avatar
            alt={user.firstName + " " + user.lastName}
            src={user.imageUrl}
          />
        </TableCell>
        <TableCell>{user.firstName}</TableCell>
        <TableCell>{user.lastName}</TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell>{user.telephone}</TableCell>
        <TableCell>{user.country}</TableCell>
        <TableCell>{user.town}</TableCell>
        <TableCell>{user.notes}</TableCell>
        {this.state.mode === "all" ? (
          <TableCell>
            {user.inviteAccepted ? (
              <Tooltip title="Invite accepted">
                <VerifiedUser />
              </Tooltip>
            ) : (
              <Tooltip title="Invite not accepted yet">
                <HourglassEmpty />
              </Tooltip>
            )}
          </TableCell>
        ) : null}
        <TableCell>
          <Tooltip title="Mange users' banks">
            <IconButton
              onClick={() => this.handleBanksListClick(user)}
              color="primary"
            >
              <AccountBalance />
            </IconButton>
          </Tooltip>
          <Tooltip title="User Profile">
            <IconButton
              onClick={() => this.handleUserProfileClick(user)}
              color="primary"
            >
              <PersonRounded />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete user">
            <IconButton
              onClick={() => this.handleDeleteUserClick(user)}
              color="secondary"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  }

  renderTableBody(users: User[]) {
    return (
      <>{users.length > 0 ? this.renderRows(users) : this.renderEmptyRow()}</>
    );
  }

  render() {
    return (
      <div className="border rounded rounded-more overflow-hidden p-5 mt-4 mt-lg-0 mb-5">
        <div>
          <h3>{this.state.headline}</h3>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Town</TableCell>
                  <TableCell>Notes</TableCell>
                  {this.state.mode === "all" ? (
                    <TableCell>Status</TableCell>
                  ) : null}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.loading ? (
                  <tr>
                    <td>
                      <Spinner />
                    </td>
                  </tr>
                ) : (
                  this.renderTableBody(this.state.items)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <AlertDialog
          open={this.state.openAlertDialog}
          handleClose={this.handleCloseAlertDialog}
          title={this.state.titleAlertDialog}
          contentText={this.state.contentTextAlertDialog}
          okButtonTitle={"Yes"}
          cancelButtonTitle={"No"}
        />
      </div>
    );
  }
}
